/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */
import React, { useCallback, useEffect, useRef, useState } from 'react'

import {
  INSERT_CHECK_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
} from '@lexical/list'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { mergeRegister } from '@lexical/utils'
import {
  $getSelection,
  $isRangeSelection,
  FORMAT_TEXT_COMMAND,
  SELECTION_CHANGE_COMMAND,
} from 'lexical'

import { BulletListIcon } from './icons/bulletList'
import { NumberListIcon } from './icons/numberList'

const LowPriority = 1

export default function ToolbarPlugin() {
  const [editor] = useLexicalComposerContext()
  const toolbarRef = useRef(null)
  const [isBold, setIsBold] = useState(false)
  const [isItalic, setIsItalic] = useState(false)
  const [isStrikethrough, setIsStrikethrough] = useState(false)

  const $updateToolbar = useCallback(() => {
    const selection = $getSelection()
    if ($isRangeSelection(selection)) {
      // Update text format
      setIsBold(selection.hasFormat('bold'))
      setIsItalic(selection.hasFormat('italic'))
      setIsStrikethrough(selection.hasFormat('strikethrough'))
    }
  }, [])

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          $updateToolbar()
        })
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_payload, _newEditor) => {
          $updateToolbar()
          return false
        },
        LowPriority
      )
    )
  }, [editor, $updateToolbar])

  const blockTypeToBlockName = {
    bullet: 'Bulleted List',
    number: 'Numbered List',
    check: 'Check List',
    paragraph: 'Normal',
  }

  const [blockType, setBlockType] = useState<keyof typeof blockTypeToBlockName>('paragraph')

  const formatList = (listType) => {
    if (listType === 'number' && blockType !== 'number') {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)
      setBlockType('number')
    } else if (listType === 'bullet' && blockType !== 'bullet') {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)
      setBlockType('bullet')
    } else if (listType === 'check' && blockType !== 'check') {
      editor.dispatchCommand(INSERT_CHECK_LIST_COMMAND, undefined)
      setBlockType('check')
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined)
      setBlockType('paragraph')
    }
  }

  return (
    <div
      className='flex items-center gap-1 p-1 bg-gray-100'
      ref={toolbarRef}
    >
      <div
        className='tooltip'
        data-tip='Bold'
      >
        <button
          type='button'
          onClick={() => {
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')
          }}
          className={'btn btn-xs text-base font-light ' + (isBold ? 'btn-active' : 'btn-ghost')}
          aria-label='Format Bold'
        >
          B
        </button>
      </div>
      <div
        className='tooltip'
        data-tip='Italic'
      >
        <button
          type='button'
          onClick={() => {
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')
          }}
          className={
            'btn btn-xs text-base font-light font-serif italic ' +
            (isItalic ? 'btn-active' : 'btn-ghost')
          }
          aria-label='Format Italics'
        >
          I
        </button>
      </div>
      <div
        className='tooltip'
        data-tip='Strikethrough'
      >
        <button
          type='button'
          onClick={() => {
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough')
          }}
          className={
            'btn btn-xs text-base font-light line-through ' +
            (isStrikethrough ? 'btn-active' : 'btn-ghost')
          }
          aria-label='Format Strikethrough'
        >
          S
        </button>
      </div>
      <div className='text-gray-300 text-sm mx-1'>|</div>
      <div
        className='tooltip'
        data-tip='Number List'
      >
        <button
          type='button'
          className='btn btn-xs'
          onClick={() => formatList('number')}
        >
          <NumberListIcon />
        </button>
      </div>
      <div
        className='tooltip'
        data-tip='Bullet List'
      >
        <button
          type='button'
          className='btn btn-xs'
          onClick={() => formatList('bullet')}
        >
          <BulletListIcon />
        </button>
      </div>
      <div className='text-gray-300 text-sm mx-1'>|</div>
    </div>
  )
}
